import { Baby, CheckCheck, CoinsIcon, ImageDownIcon, LogOut, Mars, RotateCcw, Trash2, Undo2, Venus } from "lucide-react";
import { LiaWeightSolid } from "react-icons/lia";
import React from "react";
import { twMerge } from "tailwind-merge";
import { fighterEntryHighlightClasses, weightClasses } from "../constants";
import { getEventFighterWeightClass, isMinorAtDate } from "../utils/common";

export default function EventFighterListItem({
    handleMatchingFighter,
    handleRemoveFighter,
    handlePullOutFighter,
    handleRefund,
    handleRejoinWithToken,
    handleEditWeight,
    activeTokenCount,
    eventFighter,
    eventDate,
}) {
    const fighter = eventFighter?.fighter;
    const handleDownloadFighterProfile = async (e) => {
        e?.stopPropagation?.();
        const imageUrl = fighter?.fighterPicture;
        if (!imageUrl) return;

        const safeName = (fighter?.name || "fighter")
            .toString()
            .trim()
            .replace(/\s+/g, " ");

        try {
            const response = await fetch(imageUrl, {
                mode: "cors",
                credentials: "omit",
            });
            if (!response.ok) throw new Error("Failed to download");
            const blob = await response.blob();
            const mimeSubtype = (blob?.type || "").split("/")?.[1] || "jpg";
            const fileName = `Profile Picture_${safeName}.${mimeSubtype}`;
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(blobUrl);
            return;
        } catch (e) {
            console.error("Error when downloading fighter image", e);
        }
    };

    const handlePullOutFighterClick = (e) => {
        e?.stopPropagation?.();
        handlePullOutFighter?.(eventFighter);
    }

    const handleRefundClick = (e) => {
        e?.stopPropagation?.();
        handleRefund?.(eventFighter);
    }

    const handleEditWeightClick = (e) => {
        e?.stopPropagation?.();
        handleEditWeight?.(eventFighter);
    }

    const handleRejoinWithTokenClick = (e) => {
        e?.stopPropagation?.();
        handleRejoinWithToken?.(eventFighter);
    }

    const isPulledOut = !!eventFighter?.isPulledOut;
    const isRefunded = !!eventFighter?.isRefunded;
    const isMatched = !!eventFighter?.matchWith;
    // Under-17 as of the event date (admin-only indicator). birthdate is the
    // full Firestore Timestamp from the fighter doc on the admin page.
    const isMinor = isMinorAtDate(fighter?.birthdate, eventDate);
    const needsRematchReview = eventFighter?.needsRematchReview === true;
    const entryHighlight = fighterEntryHighlightClasses[eventFighter?.entry];

    return (
        <button
            className={twMerge(
                "w-full flex flex-row justify-center items-center gap-0 relative",
                handleMatchingFighter
                    ? "cursor-pointer hover:bg-base-200"
                    : "cursor-default",
                entryHighlight?.row
            )}
            onClick={() => handleMatchingFighter?.(eventFighter)}
        >
            <img
                src={fighter?.fighterPicture}
                className="h-16 w-16 aspect-square object-cover object-center mr-4"
                alt=""
            />
            <div className="flex-center items-start w-full">
                <div className="flex flex-row items-center gap-2">
                    <p className="font-semibold text-start">{fighter?.name}</p>
                    {eventFighter?.fighter?.gender?.toUpperCase() === "M" ? (
                        <Mars className="text-blue-300" />
                    ) : eventFighter?.fighter?.gender?.toUpperCase() === "F" ? (
                        <Venus className="text-pink-300" />
                    ) : (
                        "?"
                    )}
                    {isMinor && (
                        <span
                            className="inline-flex text-warning shrink-0"
                            title="17 or under (minor)"
                        >
                            <Baby size={20} />
                        </span>
                    )}
                    {isRefunded && (
                        <span className="badge badge-sm badge-warning whitespace-nowrap">
                            Refunded
                        </span>
                    )}
                    {needsRematchReview && (
                        <span
                            className="badge badge-sm badge-error whitespace-nowrap"
                            title="This fighter's weight changed after being matched — check the bout is still valid"
                        >
                            Recheck bout
                        </span>
                    )}
                </div>
                <p className="opacity-70 text-sm w-full overflow-hidden text-start text-ellipsis">
                    <span className={twMerge("font-semibold", entryHighlight?.text)}>
                        {eventFighter.entry}
                    </span>
                    :{" "}
                    {fighter?.records?.[eventFighter.entry]?.win || 0} |{" "}
                    {fighter?.records?.[eventFighter.entry]?.loss || 0} |{" "}
                    {fighter?.records?.[eventFighter.entry]?.draw || 0},{" "}
                    {weightClasses?.[
                        getEventFighterWeightClass(eventFighter)
                    ] ?? "?"}
                </p>
            </div>
            <div className="p-2">
                {!!eventFighter?.matchWith ? <CheckCheck /> : null}
            </div>
            {handleRefund && !isRefunded && !isPulledOut && !isMatched && (
                <button
                    className="btn btn-ghost btn-square"
                    onClick={handleRefundClick}
                    title="Refund with credit (issues 1 credit; fighter stays on the public list)"
                >
                    <CoinsIcon size={24} className="text-yellow-300" />
                </button>
            )}
            {handleEditWeight && (
                <button
                    className="btn btn-ghost btn-square"
                    onClick={handleEditWeightClick}
                    title="Change weight class for this event"
                >
                    <LiaWeightSolid size={24} />
                </button>
            )}
            <button
                className="btn btn-ghost btn-square"
                onClick={(e) => handleDownloadFighterProfile(e)}
            >
                <ImageDownIcon />
            </button>
            {handleRemoveFighter && (
                <button
                    className="btn btn-ghost btn-square  btn-error"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFighter(eventFighter);
                    }}
                    title="Remove from event"
                >
                    <Trash2 className="text-red-500" />
                </button>
            )}
            {handlePullOutFighter && (
                <button
                    className="btn btn-ghost btn-square"
                    onClick={handlePullOutFighterClick}
                    title={isPulledOut ? "Undo pull out" : "Pull out fighter"}
                >
                    {isPulledOut ? <Undo2 size={24} /> : <LogOut size={24} className="text-warning" />}
                </button>
            )}
            {handleRejoinWithToken && isRefunded && (
                <button
                    className="btn btn-ghost btn-square relative"
                    onClick={handleRejoinWithTokenClick}
                    title={`Rejoin by spending 1 credit (active: ${activeTokenCount ?? 0})`}
                >
                    <RotateCcw size={24} className="text-success" />
                    {(activeTokenCount ?? 0) > 0 && (
                        <span className="badge badge-xs bg-yellow-300 text-yellow-900 border-yellow-400 absolute -top-1 -right-1">
                            {activeTokenCount}
                        </span>
                    )}
                </button>
            )}
        </button>
    );
}
